<ng-container *ngIf="viewModel$ | async as vm">
  <ng-template #infoBlock>
    <div class="p-mb-2">
      {{ vm.role === 'Sales Agent' ? 'Sales agents' : 'Estimators' }}
      ranked for the selected period.
    </div>
    <div class="p-mb-2">
      <a
        [routerLink]="['/reports', 'dynamic', 'new']"
        [queryParams]="vm.queryParams"
        target="_blank"
        rel="noopener noreferrer"
        >View Report</a
      >.
    </div>
  </ng-template>

  <app-dashboard-shell
    title="Sales Leaderboard"
    [lastReload]="vm.lastReload"
    [loading]="vm.loading"
    [showRefresh]="true"
    (refresh)="triggerReload()"
    class="sales-leaderboard-container"
  >
    <ng-container filters>
      <div class="dropdowns">
        <p-dropdown
          [ngModel]="vm.role"
          (ngModelChange)="setRole($event)"
          appendTo="body"
          [options]="roleOptions"
        >
          <ng-template pTemplate="selectedItem">
            <div class="flex align-items-center gap-2">
              <i class="pi pi-user"></i>
              <span> Role: {{ vm.role }} </span>
            </div>
          </ng-template>
        </p-dropdown>
      </div>
    </ng-container>

    <!-- ── Podium ── -->
    <div class="p-col-12" *ngIf="!vm.loading && vm.podium?.length >= 3">
      <div class="podium">

        <!-- 2nd place -->
        <div class="podium-card podium-card--2nd">
          <div class="podium-rank">2ND</div>
          <div class="podium-avatar podium-avatar--2">{{ vm.podium[1].initials }}</div>
          <div class="podium-name">{{ vm.podium[1].name }}</div>
          <div class="podium-revenue">{{ vm.podium[1].revenue }}</div>
          <div class="podium-meta">{{ vm.podium[1].jobs }} jobs · {{ vm.podium[1].avg }} avg</div>
        </div>

        <!-- 1st place -->
        <div class="podium-card podium-card--1st">
          <div class="podium-rank">
            <span class="podium-crown">👑</span> 1ST
          </div>
          <div class="podium-avatar podium-avatar--1">{{ vm.podium[0].initials }}</div>
          <div class="podium-name">{{ vm.podium[0].name }}</div>
          <div class="podium-revenue">{{ vm.podium[0].revenue }}</div>
          <div class="podium-meta">{{ vm.podium[0].jobs }} jobs · {{ vm.podium[0].avg }} avg</div>
          <div class="podium-lead" *ngIf="vm.podium[0].leadOver">{{ vm.podium[0].leadOver }}</div>
        </div>

        <!-- 3rd place -->
        <div class="podium-card podium-card--3rd">
          <div class="podium-rank">3RD</div>
          <div class="podium-avatar podium-avatar--3">{{ vm.podium[2].initials }}</div>
          <div class="podium-name">{{ vm.podium[2].name }}</div>
          <div class="podium-revenue">{{ vm.podium[2].revenue }}</div>
          <div class="podium-meta">{{ vm.podium[2].jobs }} jobs · {{ vm.podium[2].avg }} avg</div>
        </div>

      </div>
    </div>

    <!-- ── Table ── -->
    <div class="p-col-12">
      <div class="dashboard-card">
        <div class="dashboard-card-header">
          <div>
            <h4 class="p-mb-1">
              {{ vm.tableTitle
              }}<app-error-icon *ngIf="vm.error" [error]="vm.error"></app-error-icon>
            </h4>
          </div>
          <app-dashboard-info-v2>
            <ng-container *ngTemplateOutlet="infoBlock"></ng-container>
          </app-dashboard-info-v2>
        </div>

        <p-blockUI
          [target]="leaderboardTable"
          [blocked]="vm.loading"
          [autoZIndex]="false"
        >
          <i class="pi pi-spin pi-spinner" style="font-size: 3rem"></i>
        </p-blockUI>

        <app-blockable #leaderboardTable>
          <ng-container *ngIf="!vm.isEmpty">
            <div class="table-scroll">
            <table class="events-table sales-leaderboard-table">
              <thead>
                <tr>
                  <th
                    [class.col-hover]="hoveredColIndex === -1"
                    (mouseenter)="hoveredColIndex = -1"
                    (mouseleave)="hoveredColIndex = null"
                  >{{ vm.headers[0].label }}</th>
                  <th
                    *ngFor="let col of tableColumns; index as j"
                    class="sortable"
                    [class.leaderboard-highlight]="col.key === vm.sortKey"
                    [class.col-hover]="hoveredColIndex === j"
                    [pTooltip]="vm.role === 'Estimator' && col.estimatorTooltip ? col.estimatorTooltip : col.tooltip"
                    tooltipPosition="top"
                    (mouseenter)="hoveredColIndex = j"
                    (mouseleave)="hoveredColIndex = null"
                    (click)="toggleSort(col.key, vm.sortKey, vm.sortDesc)"
                  >
                    <span class="dashed-underline" [innerHTML]="col.label"></span>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let row of vm.rows; index as i">
                  <td
                    [class.col-hover]="hoveredColIndex === -1"
                    (mouseenter)="hoveredColIndex = -1"
                    (mouseleave)="hoveredColIndex = null"
                  >
                    <a
                      class="agent-link"
                      (click)="openAgentReport(vm.userIds[i], vm.queryParams, vm.role)"
                    >{{ row[0] }}</a>
                  </td>
                  <td
                    *ngFor="let cell of row | slice:1; index as j"
                    [class.leaderboard-highlight]="tableColumns[j].key === vm.sortKey"
                    [class.col-hover]="hoveredColIndex === j"
                    (mouseenter)="hoveredColIndex = j"
                    (mouseleave)="hoveredColIndex = null"
                  >
                    <a
                      *ngIf="tableColumns[j].drillDown; else plainCell"
                      class="metric-link"
                      (click)="openMetricReport(tableColumns[j], vm.userIds[i], vm.queryParams, vm.role)"
                    >{{ cell }}</a>
                    <ng-template #plainCell>{{ cell }}</ng-template>
                  </td>
                </tr>
                <tr *ngIf="!vm.loading && vm.totalsRow" class="summary-row totals-row">
                  <td>{{ vm.totalsRow[0] }}</td>
                  <td
                    *ngFor="let cell of vm.totalsRow | slice:1; index as j"
                    [class.leaderboard-highlight]="tableColumns[j].key === vm.sortKey"
                    [class.col-hover]="hoveredColIndex === j"
                  >{{ cell }}</td>
                </tr>
                <tr *ngIf="!vm.loading && vm.meanRow" class="summary-row mean-row">
                  <td>{{ vm.meanRow[0] }}</td>
                  <td
                    *ngFor="let cell of vm.meanRow | slice:1; index as j"
                    [class.leaderboard-highlight]="tableColumns[j].key === vm.sortKey"
                    [class.col-hover]="hoveredColIndex === j"
                  >{{ cell }}</td>
                </tr>
              </tbody>
            </table>
            </div>
            <p
              *ngIf="vm.hiddenRowsNote"
              class="hidden-rows-note"
              [class.has-names]="vm.hiddenRowsTooltip"
              [pTooltip]="vm.hiddenRowsTooltip"
              tooltipPosition="top"
              tooltipStyleClass="hidden-rows-tooltip"
            >{{ vm.hiddenRowsNote }}</p>
          </ng-container>
          <app-dashboard-empty-state
            *ngIf="vm.isEmpty"
            text="No sales activity in the selected period"
          ></app-dashboard-empty-state>
        </app-blockable>
      </div>
    </div>
  </app-dashboard-shell>
</ng-container>
